import React, { useCallback, useEffect, useRef, useState } from "react";
import CallBatchDetails from "./CallBatchDetails";
import { CALL_STATUSES, getCallStatus, toApiOutcome } from "./statusConfig";
import { useLeadDrawerData } from "./useLeadDrawerData";
import { fetchMyLeads, saveLeadOutcome } from "../callWorkspaceApi";
import { getToken, toCallWorkspaceLead } from "../callWorkspace.utils";
import { getApiErrorMessage } from "utils/getApiErrorMessage";

const TABS = [
  { key: "active", label: "Active" },
  { key: "follow_up", label: "Follow-up" },
  { key: "called", label: "Called" },
  { key: "blacklist", label: "Don't Call" },
];
const emptyPagination = () => ({ current_page: 1, last_page: 1, total: 0 });
const initialDrawerState = () => ({ expanded: false, activeTab: "summary", resources: {}, loading: {}, activityLimit: 20 });

const CallBatchDetailsContainer = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: null, follow_up: null, called: null, blacklist: null });
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusChange, setStatusChange] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [drawer, setDrawer] = useState(initialDrawerState);
  const requestId = useRef(0);
  const drawerData = useLeadDrawerData(!!selectedLead, selectedLead);

  const loadQueue = useCallback((tab, page, query) => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    return fetchMyLeads(getToken(), { tab, search: query || undefined, page: page || 1, page_size: 20 })
      .then((response) => {
        if (requestId.current !== id) return;
        const payload = response.data || {};
        const result = payload.leads || {};
        setRows((result.data || []).map((lead) => toCallWorkspaceLead(lead, tab)));
        setCounts(payload.counts || {});
        setPagination({ current_page: result.current_page || 1, last_page: result.last_page || 1, total: result.total || 0 });
      })
      .catch((apiError) => {
        if (requestId.current === id) setError(getApiErrorMessage(apiError));
      })
      .finally(() => {
        if (requestId.current === id) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    document.title = "My Calls | Call Workspace";
    loadQueue(activeTab, 1, appliedSearch);
    return () => { requestId.current += 1; };
  }, [activeTab, appliedSearch, loadQueue]);

  const changeStatus = (rowId, status) => {
    const lead = rows.find((item) => item.id === rowId);
    if (!lead || !getCallStatus(status)) return;
    setStatusError("");
    setStatusChange({ lead, status });
  };
  const saveStatus = ({ status, reason, dateTime, notes }) => {
    if (!statusChange || statusSaving) return Promise.resolve(false);
    const nextStatus = status || statusChange.status;
    const definition = getCallStatus(nextStatus);
    const outcome = toApiOutcome(nextStatus, reason);
    if (!definition || !outcome) {
      setStatusError("Select a valid call status.");
      return Promise.resolve(false);
    }
    setStatusSaving(true);
    setStatusError("");
    const lead = statusChange.lead;
    return saveLeadOutcome(getToken(), lead.taskId, {
      outcome,
      reason: reason || null,
      remarks: notes || null,
      next_follow_up_at: outcome === "follow_up" && dateTime
        ? new Date(dateTime).toISOString()
        : null,
    })
      .then(() => {
        setStatusChange(null);
        if (selectedLead && selectedLead.taskId === lead.taskId) drawerData.refreshDetails();
      return loadQueue(activeTab, pagination.current_page, appliedSearch).then(() => true);
      })
      .catch((apiError) => {
        setStatusError(getApiErrorMessage(apiError));
        return false;
      })
      .finally(() => setStatusSaving(false));
  };
  const openStatusChange = (lead) => {
    const rawOutcome = (drawerData.details && drawerData.details.task && drawerData.details.task.outcome) || lead.outcome;
  const statusByOutcome = { not_called: "Not called", no_answer: "No answer", follow_up: "Follow-up", interested: "Called", closed: "Called", blacklisted: "Don't Call" };
    setStatusError("");
    setStatusChange({ lead, status: statusByOutcome[rawOutcome] || "Not called" });
  };
  const selectLead = (lead) => {
    setDrawer(initialDrawerState());
    setSelectedLead(lead);
  };
  const closeLead = () => {
    setSelectedLead(null);
    setDrawer(initialDrawerState());
  };
  const selectDrawerTab = (tab) => {
    setDrawer((current) => ({ ...current, activeTab: tab }));
    if (tab === "medicine" || tab === "lab") drawerData.load("orders");
    if (tab === "consultations") drawerData.load("consultations");
    if (tab === "finance") drawerData.load("finance");
    if (tab === "claims") {
      drawerData.load("claims");
      drawerData.load("transactions");
      drawerData.loadPolicies();
    }
  };
  return (
    <CallBatchDetails
      tabs={TABS}
      activeTab={activeTab}
      rows={rows}
      search={search}
      onSearchChange={setSearch}
      counts={counts}
      pagination={pagination}
      loading={loading}
      error={error}
      onRetry={() => loadQueue(activeTab, pagination.current_page, appliedSearch)}
      onPageChange={(page) => loadQueue(activeTab, page, appliedSearch)}
      onTabChange={setActiveTab}
      selectedLead={selectedLead}
      onSelectLead={selectLead}
      onCloseLead={closeLead}
      onStatusChange={changeStatus}
      statusChange={statusChange}
      onCloseStatusChange={() => { setStatusChange(null); setStatusError(""); }}
      onSaveStatus={saveStatus}
      statusSaving={statusSaving}
      statusError={statusError}
      onOpenStatusChange={openStatusChange}
      drawer={drawer}
      drawerData={drawerData}
      onToggleDrawerDetails={() => setDrawer((current) => ({ ...current, expanded: !current.expanded }))}
      onSelectDrawerTab={selectDrawerTab}
      onLoadMoreActivity={() => setDrawer((current) => ({ ...current, activityLimit: current.activityLimit + 20 }))}
    />
  );
};

export default CallBatchDetailsContainer;
